// Recognising and cleaning a return from Keycloak in the query string (SF-167, Q-328).
//
// A manual required-action link (docs/runbooks/keycloak-sign-in-policy.md) uses
// response_type=code without response_mode, so Keycloak returns in the QUERY. keycloak-js only
// reads its own FRAGMENT callbacks, so with onLoad: 'login-required' it restarted the login with
// redirect_uri = location.href, carrying the old callback; Keycloak's redirect then outgrew nginx's
// header buffer (502). The SPA therefore removes a recognised query return before keycloak-js
// starts. keycloak-js consumes and removes its own fragment callbacks; the fragment is never
// touched here.
//
// A return is recognised only when `iss` equals one of the issuers this app trusts exactly, so a
// foreign issuer with the same realm suffix, or a stray kc_action_status, removes nothing. Its
// outcome is advisory: anyone can craft such a URL, so the caller must not present it as verified.

export type ActionName = 'CONFIGURE_TOTP' | 'webauthn-register';
export type ActionStatus = 'success' | 'cancelled' | 'error';

export interface ActionReturn {
  /** True when the query held a Keycloak return from a trusted issuer; cleanUrl then differs. */
  recognised: boolean;
  /** 'action' for a required-action return, 'code' or 'error' for a plain one. */
  kind: 'action' | 'code' | 'error' | null;
  /** Allowlisted action and status; null when missing, unknown or duplicated. */
  action: ActionName | null;
  status: ActionStatus | null;
  /** The same URL without the return's keys: path, other query keys and fragment unchanged. */
  cleanUrl: string;
}

// The keys of a Keycloak return. They are reserved for it: an app URL must not use them.
const RETURN_KEYS = [
  'code',
  'session_state',
  'iss',
  'kc_action',
  'kc_action_status',
  'error',
  'error_description',
  'error_uri',
  'state',
];
const ACTIONS: ReadonlySet<string> = new Set<ActionName>(['CONFIGURE_TOTP', 'webauthn-register']);
const STATUSES: ReadonlySet<string> = new Set<ActionStatus>(['success', 'cancelled', 'error']);

const trimSlash = (value: string): string => value.replace(/\/+$/, '');

/** The issuers a return may come from: Keycloak's advertised issuer and the configured one. */
export const trustedIssuers = (
  keycloakUrl: string,
  realm: string,
  discoveredIssuer?: string | null,
): string[] => {
  const configured = `${trimSlash(keycloakUrl)}/realms/${realm}`;
  return discoveredIssuer ? [trimSlash(discoveredIssuer), configured] : [configured];
};

/** True when the URL's query could be a Keycloak return, so it is worth fetching the issuer. */
export const mayBeReturn = (href: string): boolean => new URL(href).searchParams.has('iss');

const notRecognised = (href: string): ActionReturn => ({
  recognised: false,
  kind: null,
  action: null,
  status: null,
  cleanUrl: href,
});

export const parseActionReturn = (href: string, issuers: string[]): ActionReturn => {
  const url = new URL(href);
  const query = url.searchParams;
  const issuerValues = query.getAll('iss');
  if (issuerValues.length !== 1) return notRecognised(href);
  const trusted = new Set(issuers.map(trimSlash));
  if (!trusted.has(trimSlash(issuerValues[0]))) return notRecognised(href);

  let kind: ActionReturn['kind'] = null;
  if (query.has('kc_action_status')) kind = 'action';
  else if (query.has('error')) kind = 'error';
  else if (query.has('code') && query.has('session_state')) kind = 'code';
  if (!kind) return notRecognised(href);

  const duplicated = RETURN_KEYS.some((key) => query.getAll(key).length > 1);
  const action = query.get('kc_action');
  const status = query.get('kc_action_status');

  for (const key of RETURN_KEYS) query.delete(key);
  url.search = query.toString();

  return {
    recognised: true,
    kind,
    action: !duplicated && kind === 'action' && action && ACTIONS.has(action) ? (action as ActionName) : null,
    status: !duplicated && kind === 'action' && status && STATUSES.has(status) ? (status as ActionStatus) : null,
    cleanUrl: url.toString(),
  };
};

/**
 * The issuer Keycloak advertises for the realm, through the same URL the app signs in with. Bounded:
 * a stalled discovery must not hold sign-in, so after `timeoutMs` (body included) it gives null.
 */
export const fetchIssuer = async (keycloakUrl: string, realm: string, timeoutMs = 3000): Promise<string | null> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve(null);
    }, timeoutMs);
  });
  const discover = async (): Promise<string | null> => {
    const response = await fetch(`${trimSlash(keycloakUrl)}/realms/${realm}/.well-known/openid-configuration`,
      { signal: controller.signal });
    if (!response.ok) return null;
    const document = await response.json();
    return typeof document?.issuer === 'string' ? document.issuer : null;
  };
  try {
    return await Promise.race([discover(), deadline]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};
